import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import { mensagemAmigavel } from '@/lib/mensagemAmigavel';
import { garantirUsuario } from '@/lib/usuario';
import { guardarDestino } from '@/lib/destino';
import CartaoEvento from './components/CartaoEvento';
import Dica from '@/components/feature/Dica';
import EmptyState from '@/components/ui/EmptyState';
import Accordion from '@/components/ui/Accordion';
import ListaEntrada from '@/components/movimento/ListaEntrada';
import { montarListas } from './utils';
import type { CarteiraDaConta, CarteiraDoAparelho, IngressoLinha } from './utils';
import type { ListasCompras } from './types';
import type { CapaDoEvento } from './components/CartaoEvento';

const CHAVE_CARTEIRA = 'carteira:';

// Carteiras guardadas só neste aparelho (chaves "carteira:<evento>").
async function carteirasDoAparelho(ignorar: Set<number>): Promise<CarteiraDoAparelho[]> {
  const pares: { codigo: string; eventoId: number }[] = [];
  try {
    for (let i = 0; i < localStorage.length; i += 1) {
      const k = localStorage.key(i);
      if (!k || !k.startsWith(CHAVE_CARTEIRA)) continue;
      const eventoId = Number(k.slice(CHAVE_CARTEIRA.length));
      const codigo = localStorage.getItem(k);
      if (!Number.isFinite(eventoId) || !codigo || ignorar.has(eventoId)) continue;
      pares.push({ codigo, eventoId });
    }
  } catch {
    return [];
  }

  const resultados = await Promise.all(
    pares.map(async (p): Promise<CarteiraDoAparelho | null> => {
      try {
        const { data } = await supabase.rpc('loja_carteira_publica', { p_carteira: p.codigo });
        const d = data as {
          carteira?: { evento_nome?: string; evento_inicio?: string | null };
          itens?: { quantidade?: number }[];
        } | null;
        if (!d?.carteira) return null;
        const fichas = (d.itens ?? []).reduce((soma, i) => soma + (i.quantidade ?? 0), 0);
        return {
          codigo: p.codigo,
          eventoId: p.eventoId,
          eventoNome: d.carteira.evento_nome ?? 'Evento',
          eventoInicio: d.carteira.evento_inicio ?? null,
          fichas,
        };
      } catch {
        return null;
      }
    }),
  );

  return resultados.filter((c): c is CarteiraDoAparelho => c != null);
}

export default function MeusIngressosPage() {
  const navigate = useNavigate();
  const { user, pronto } = useAuth();

  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [listas, setListas] = useState<ListasCompras>({ proximos: [], anteriores: [] });
  // Arte de cada evento (mostrada inteira, sem cortar).
  const [capas, setCapas] = useState<Map<number, CapaDoEvento>>(new Map());
  const [reload, setReload] = useState(0);

  const voltarAoEntrar = useCallback(() => {
    guardarDestino();
    navigate(`/entrar?voltar=${encodeURIComponent('/meus-ingressos')}`, { replace: true });
  }, [navigate]);

  useEffect(() => {
    if (!pronto) return;
    let ativo = true;

    async function carregar() {
      setLoading(true);
      setErro(null);
      try {
        let ingressos: IngressoLinha[] = [];
        let daConta: CarteiraDaConta[] = [];

        if (user) {
          await garantirUsuario();

          const { data: ingressosData, error: errIngressos } = await supabase
            .from('vw_meus_ingressos')
            .select('*');
          if (errIngressos) throw errIngressos;
          ingressos = (ingressosData as IngressoLinha[]) || [];

          const { data: carteirasData } = await supabase.rpc('minhas_carteiras');
          daConta = ((carteirasData as CarteiraDaConta[]) || []).filter(
            (c) => (c.itens_loja ?? 0) > 0,
          );
        }

        const doAparelho = await carteirasDoAparelho(
          new Set(daConta.map((c) => c.evento_id)),
        );

        const montado = montarListas(ingressos, daConta, doAparelho);
        if (!ativo) return;

        // Sem login e sem nada guardado neste aparelho: manda entrar.
        if (!user && montado.proximos.length === 0 && montado.anteriores.length === 0) {
          voltarAoEntrar();
          return;
        }

        setListas(montado);

        // Artes dos eventos desta lista (só leitura).
        const ids = [...montado.proximos, ...montado.anteriores]
          .map((e) => e.eventoId)
          .filter((n): n is number => typeof n === 'number' && n > 0);
        if (ids.length > 0) {
          const { data: capasData } = await supabase
            .from('eventos')
            .select('id, imagem_mobile_url, imagem_desktop_url')
            .in('id', ids);
          const mapa = new Map<number, CapaDoEvento>();
          (
            (capasData as {
              id: number;
              imagem_mobile_url: string | null;
              imagem_desktop_url: string | null;
            }[]) || []
          ).forEach((ev) => {
            if (!ev.imagem_mobile_url && !ev.imagem_desktop_url) return;
            mapa.set(ev.id, {
              mobile: ev.imagem_mobile_url ?? ev.imagem_desktop_url,
              desktop: ev.imagem_desktop_url ?? ev.imagem_mobile_url,
            });
          });
          if (ativo) setCapas(mapa);
        }
      } catch (e) {
        if (ativo) {
          setErro(
            mensagemAmigavel(e, 'Não foi possível carregar seus ingressos. Tente novamente.'),
          );
        }
      } finally {
        if (ativo) setLoading(false);
      }
    }

    carregar();
    return () => {
      ativo = false;
    };
  }, [reload, pronto, user, voltarAoEntrar]);

  const vazio = listas.proximos.length === 0 && listas.anteriores.length === 0;

  if (loading) {
    return (
      <div className="min-h-screen bg-background-100">
        <div className="mx-auto w-full max-w-[720px] px-5 py-6">
          <div className="h-8 w-56 animate-pulse rounded-xl bg-background-200/70" />
          <div className="mt-2 h-4 w-72 animate-pulse rounded-xl bg-background-200/70" />
          <div className="mt-6 space-y-4">
            <div className="h-4 w-40 animate-pulse rounded bg-background-200/70" />
            <div className="h-24 w-full animate-pulse rounded-[18px] bg-background-200/70" />
            <div className="h-24 w-full animate-pulse rounded-[18px] bg-background-200/70" />
          </div>
        </div>
      </div>
    );
  }

  if (erro) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background-100 px-4 py-8">
        <div className="w-full max-w-[420px] rounded-[18px] bg-background-50 p-6 text-center shadow-pcard">
          <span className="mx-auto flex h-14 w-14 items-center justify-center text-foreground-300">
            <i className="ri-error-warning-line text-[36px]" aria-hidden />
          </span>
          <h1 className="mt-4 text-[20px] font-extrabold text-foreground-950">
            Não foi possível carregar
          </h1>
          <p className="mt-2 text-[14px] text-foreground-600">{erro}</p>
          <button
            type="button"
            onClick={() => setReload((r) => r + 1)}
            className="aperta mt-6 h-14 w-full cursor-pointer whitespace-nowrap rounded-[18px] bg-primary-500 text-[15px] font-bold text-white"
          >
            Tentar de novo
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-100">
      <div className="mx-auto w-full max-w-[720px] px-5 py-6">
        <div className="flex items-center gap-2">
          <h1 className="text-[22px] font-extrabold text-foreground-950">
            Meus ingressos e fichas
          </h1>
          <Dica id="meus-ingressos" />
        </div>
        <p className="mt-1 text-[14px] text-foreground-600">
          Seus ingressos e as fichas de cada evento, num lugar só.
        </p>

        {!user && (
          <Link
            to={`/entrar?voltar=${encodeURIComponent('/meus-ingressos')}`}
            className="aperta mt-4 flex items-center gap-3 rounded-[18px] bg-accent-100 px-4 py-3 text-[14px] font-semibold text-accent-900"
          >
            <span className="flex h-5 w-5 items-center justify-center">
              <i className="ri-user-3-line" aria-hidden />
            </span>
            Entre para ver também os ingressos da sua conta
            <span className="ml-auto flex h-4 w-4 items-center justify-center">
              <i className="ri-arrow-right-s-line text-lg" aria-hidden />
            </span>
          </Link>
        )}

        {vazio ? (
          <div className="mt-6">
            <EmptyState
              icone="ri-ticket-2-line"
              titulo="Nada por aqui ainda"
              descricao="Quando você comprar ingressos ou fichas, os eventos aparecem nesta lista."
              acao={
                <Link
                  to="/"
                  className="aperta inline-flex h-12 items-center whitespace-nowrap rounded-[18px] border border-background-200 bg-background-50 px-5 text-[14px] font-semibold text-foreground-800"
                >
                  Ver eventos
                </Link>
              }
            />
          </div>
        ) : (
          <>
            {listas.proximos.length > 0 && (
              <section className="mt-6">
                <h2 className="text-[12px] font-bold uppercase tracking-wide text-foreground-400">
                  Próximos
                </h2>
                <ListaEntrada
                  itens={listas.proximos}
                  chave={(evento) => evento.chave}
                  className="mt-2 space-y-3"
                >
                  {(evento) => (
                    <CartaoEvento
                      evento={evento}
                      capa={evento.eventoId != null ? capas.get(evento.eventoId) ?? null : null}
                    />
                  )}
                </ListaEntrada>
              </section>
            )}

            {listas.anteriores.length > 0 && (
              <section className="mt-6">
                <Accordion
                  titulo="Anteriores"
                  resumo={`${listas.anteriores.length}`}
                >
                  <ListaEntrada
                    itens={listas.anteriores}
                    chave={(evento) => evento.chave}
                    className="space-y-3"
                  >
                    {(evento) => (
                      <CartaoEvento
                        evento={evento}
                        capa={evento.eventoId != null ? capas.get(evento.eventoId) ?? null : null}
                      />
                    )}
                  </ListaEntrada>
                </Accordion>
              </section>
            )}
          </>
        )}
      </div>
    </div>
  );
}